import { hashObject } from '../utils/hash';
import { BaseWebComponentTranslator } from './BaseWebComponentTranslator';
import { ServerResponseKind, TemplateState } from '../types/common/types';
import { ErrorDialogStrings } from '../types';
import { GzDialogWebComponent } from '../ui/gzDialog';
import { GzDialog } from '../ui/gzDialog/component/GzDialog';

interface HTMLElementWithContent extends HTMLElement {
  content: Node;
}

export class BaseWebComponentView {
  public dom: any;
  protected shadow: any;
  protected dependenciesInjected: boolean;
  protected dependencies: any;
  public isConnected: boolean;
  protected latestData: any;
  protected latestDataHash: number;
  protected stringsTranslator: BaseWebComponentTranslator;
  protected translations: any;
  protected commonStrings: any;

  constructor(shadow: ShadowRoot, template: HTMLElement) {
    this.shadow = shadow;
    this.shadow.appendChild((template as HTMLElementWithContent).content.cloneNode(true));
    this.initialSetup();
  }

  initialSetup() {
    this.dependenciesInjected = false;
    this.dependencies = {};
    this.dom = {};
    this.isConnected = false;
  }

  injectDependencies(dependencies: any) {
    this.dependencies = dependencies;
    this.dependenciesInjected = true;
  }

  getTranslations() {
    // to be implemented
  }

  setTranslatedStrings() {
    // to be implemented
  }

  connected() {
    this.stringsTranslator = BaseWebComponentTranslator.getInstance();
    this.stringsTranslator.injectDependencies(this.dependencies);
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.getDomElements();
    this.isConnected = true;
  }

  disconnected() {
    this.dom = {};
    this.isConnected = false;

    this.onDisconnect();
  }

  render(data: any) {
    if (!this.isConnected) {
      return;
    }
    this.buildView(data);
  }

  start() {
    this.connected();
  }

  stop() {
    this.disconnected();
  }

  protected onDisconnect() {}

  getDomElements() {
    // to be implemented
  }

  buildView(data: any) {
    if (data.kind === ServerResponseKind.ERROR) {
      console.error(data.error);
      this.buildErrorView(data.error);
      return;
    }
    this.buildSuccessView(data.response);
  }

  buildSuccessView(data: any) {
    throw new Error('Not implemented');
  }

  buildErrorView(error: Error) {
    throw new Error('Not implemented');
  }

  updateLatestData(data: any) {
    const hash: number = hashObject(data);
    if (this.latestDataHash === hash) {
      return false;
    }

    this.latestData = data;
    this.latestDataHash = hash;
    return true;
  }

  setTemplateState(element: HTMLElement, state: TemplateState) {
    if (!element.classList.contains(state)) {
      element.classList.add(state);
    }
  }

  resetTemplateState(element: HTMLElement) {
    const templateStates = [
      TemplateState.SCROLL_LOADING,
      TemplateState.LOADING,
      TemplateState.DISABLED,
      TemplateState.EMPTY,
      TemplateState.ERROR,
      TemplateState.MISSING,
      TemplateState.MISSING_MSP,
      TemplateState.TIMEOUT,
    ];
    element.classList.remove(...templateStates);
  }

  setElementClass(element: HTMLElement, className: string) {
    if (!element.classList.contains(className)) {
      element.classList.add(className);
    }
  }

  removeElementClass(element: HTMLElement, className: string) {
    element.classList.remove(className);
  }

  protected buildDialogElement(strings: ErrorDialogStrings): GzDialogWebComponent {
    const { title, message, actionButtonString } = strings;

    const dialogBody = document.createElement('div');
    dialogBody.classList.add('d-flex', 'flex-row', 'justify-space-between', 'align-items-center');
    dialogBody.innerHTML = `
        <span class="icon icon-unsupported_sandbox dialog-icon danger"></span>
        <p>${message}</p>
    `;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `<gz-button>${actionButtonString}</gz-button>`;

    const actionButton = dialogFooter.querySelector('gz-button');

    const dialogConfigs: GzDialog = {
      headline: title,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    if (actionButton) {
      actionButton.addEventListener('click', () => dialog.close());
    }

    document.body.appendChild(dialog);
    dialog.open();

    return dialog;
  }

  setLoadingState(): void {
    // do nothing here;
  }

  removeLoadingState(): void {
    // do nothing here;
  }

  clearWrapper(): void {
    //do nothing here
  }
}
